import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { 
    Button, 
    Typography, 
    Paper, 
    CircularProgress, 
    Grid, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
    Alert,
    Box,
    Card,
    CardContent,
    CardActions,
    Divider
} from '@mui/material';
import { 
    PlayArrow as GenerateIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledButton = styled(Button)(({ theme }) => ({
    margin: theme.spacing(1),
    backgroundColor: '#e8442d',
    '&:hover': {
        backgroundColor: '#c73e26',
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#333333',
    color: 'white',
    margin: theme.spacing(2),
    '& .MuiCardContent-root': {
        padding: theme.spacing(2),
    },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: '#e8442d',
    marginBottom: theme.spacing(2),
}));

const BatteryScopeC = () => {
    const [plots, setPlots] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedPlot, setSelectedPlot] = useState('');
    const [parameters, setParameters] = useState({
        start_freq: 200000,
        end_freq: 6000000000,
        num_points: 1000,
        num_samples: 5
    });
    const [plotConfig, setPlotConfig] = useState({});

    // API base URL
    const LOCAL_API_BASE_URL = 'http://localhost:5001/api';

    useEffect(() => {
        // Load plot configuration on component mount
        fetchPlotConfig();
    }, []);

    const fetchPlotConfig = async () => {
        try {
            const response = await fetch(`${LOCAL_API_BASE_URL}/plot-config`);
            const data = await response.json();
            setPlotConfig(data);
        } catch (error) {
            console.error('Error fetching plot config:', error);
        }
    };

    const generatePlots = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${LOCAL_API_BASE_URL}/generate-plots`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(parameters)
            });

            const data = await response.json();
            
            if (data.success) {
                setPlots(data.plots);
                if (Object.keys(data.plots).length > 0) {
                    setSelectedPlot(Object.keys(data.plots)[0]);
                }
            } else {
                setError(data.error || 'Failed to generate plots');
            }
        } catch (error) {
            setError('Failed to connect to plot generator server. Please ensure the server is running on port 5001.');
        } finally {
            setLoading(false);
        }
    };

    const generateLivePlots = async () => {
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch(`${LOCAL_API_BASE_URL}/live-plots`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    start_freq: parameters.start_freq,
                    end_freq: parameters.end_freq,
                    num_points: parameters.num_points
                })
            });

            const data = await response.json();
            
            if (data.success) {
                setPlots(data.plots);
                if (Object.keys(data.plots).length > 0) {
                    setSelectedPlot(Object.keys(data.plots)[0]);
                }
            } else {
                setError(data.error || 'Failed to generate live plots');
            }
        } catch (error) {
            setError('Failed to connect to plot generator server or PocketVNA device not available.');
        } finally {
            setLoading(false);
        }
    };

    const downloadData = async () => {
        try {
            const response = await fetch(`${LOCAL_API_BASE_URL}/download-data`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(parameters)
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `batteryscope_c_data_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError('Failed to download data');
            }
        } catch (error) {
            setError('Failed to download data');
        }
    };

    const handleParameterChange = (param, value) => {
        setParameters(prev => ({
            ...prev,
            [param]: value
        }));
    };

    const plotTypes = [
        { key: 'amplitude_s11_s22', label: 'S11/S22 Amplitude' },
        { key: 'amplitude_difference', label: 'Amplitude Difference' },
        { key: 'phase_s11_s22', label: 'S11/S22 Phase' },
        { key: 'phase_difference', label: 'Phase Difference' },
        { key: 's12_amplitude_phase', label: 'S12 Amplitude/Phase' },
        { key: 's12_difference', label: 'S12 Difference' }
    ];

    return (
        <div className="text-white pt-4 flex flex-col gap-6 items-center justify-center min-h-screen">
            <StyledTypography variant="h4" component="h1" align="center">
                BatteryScope-C: S-Parameter Analysis
            </StyledTypography>
            
            <Typography variant="body1" align="center" sx={{ maxWidth: '800px', mb: 4 }}>
                Advanced S-parameter analysis for battery impedance characterization. 
                Generate sample plots for testing or connect to PocketVNA hardware for live measurements.
            </Typography>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ width: '100%', maxWidth: '800px', mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Parameters Configuration */}
            <StyledCard sx={{ width: '100%', maxWidth: '800px' }}>
                <CardContent>
                    <StyledTypography variant="h6" gutterBottom>
                        <SettingsIcon sx={{ mr: 1 }} />
                        Measurement Parameters
                    </StyledTypography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Start Frequency (Hz)"
                                type="number"
                                value={parameters.start_freq}
                                onChange={(e) => handleParameterChange('start_freq', parseFloat(e.target.value))}
                                variant="outlined"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { color: 'white' },
                                    '& .MuiInputLabel-root': { color: 'white' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="End Frequency (Hz)"
                                type="number"
                                value={parameters.end_freq}
                                onChange={(e) => handleParameterChange('end_freq', parseFloat(e.target.value))}
                                variant="outlined"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { color: 'white' },
                                    '& .MuiInputLabel-root': { color: 'white' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Number of Points"
                                type="number"
                                value={parameters.num_points}
                                onChange={(e) => handleParameterChange('num_points', parseInt(e.target.value))}
                                variant="outlined"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { color: 'white' },
                                    '& .MuiInputLabel-root': { color: 'white' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Number of Samples"
                                type="number"
                                value={parameters.num_samples}
                                onChange={(e) => handleParameterChange('num_samples', parseInt(e.target.value))}
                                variant="outlined"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { color: 'white' },
                                    '& .MuiInputLabel-root': { color: 'white' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <StyledButton
                        variant="contained"
                        startIcon={<GenerateIcon />}
                        onClick={generatePlots}
                        disabled={loading}
                    >
                        Generate Sample Plots
                    </StyledButton>
                    
                    <StyledButton
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={generateLivePlots}
                        disabled={loading}
                    >
                        Generate Live Plots
                    </StyledButton>
                    
                    <StyledButton
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={downloadData}
                        disabled={loading || Object.keys(plots).length === 0}
                    >
                        Download Data
                    </StyledButton>
                </CardActions>
            </StyledCard>

            {/* Loading Indicator */}
            {loading && (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress sx={{ color: '#e8442d' }} />
                    <Typography>Generating plots...</Typography>
                </Box>
            )}

            {/* Plot Selection */}
            {Object.keys(plots).length > 0 && (
                <StyledCard sx={{ width: '100%', maxWidth: '800px' }}>
                    <CardContent>
                        <StyledTypography variant="h6" gutterBottom>
                            Plot Selection
                        </StyledTypography>
                        
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: 'white' }}>Select Plot Type</InputLabel>
                            <Select
                                value={selectedPlot}
                                onChange={(e) => setSelectedPlot(e.target.value)}
                                sx={{ 
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }
                                }}
                            >
                                {plotTypes.map((plotType) => (
                                    <MenuItem key={plotType.key} value={plotType.key}>
                                        {plotType.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </CardContent>
                </StyledCard>
            )}

            {/* Plot Display */}
            {selectedPlot && plots[selectedPlot] && (
                <Box sx={{ width: '100%', maxWidth: '1200px', height: '600px' }}>
                    <Plot
                        data={plots[selectedPlot].data}
                        layout={{
                            ...plots[selectedPlot].layout,
                            paper_bgcolor: 'rgba(0,0,0,0)',
                            plot_bgcolor: 'rgba(0,0,0,0)',
                            font: { color: 'white' },
                            xaxis: { ...plots[selectedPlot].layout.xaxis, color: 'white' },
                            yaxis: { ...plots[selectedPlot].layout.yaxis, color: 'white' },
                            xaxis2: { ...plots[selectedPlot].layout.xaxis2, color: 'white' },
                            yaxis2: { ...plots[selectedPlot].layout.yaxis2, color: 'white' }
                        }}
                        config={{ responsive: true }}
                        style={{ width: '100%', height: '100%' }}
                    />
                </Box>
            )}

            {/* Instructions */}
            {Object.keys(plots).length === 0 && !loading && (
                <StyledCard sx={{ width: '100%', maxWidth: '800px' }}>
                    <CardContent>
                        <StyledTypography variant="h6" gutterBottom>
                            Getting Started
                        </StyledTypography>
                        <Typography variant="body2" paragraph>
                            1. Configure your measurement parameters above
                        </Typography>
                        <Typography variant="body2" paragraph>
                            2. Click "Generate Sample Plots" to create test data
                        </Typography>
                        <Typography variant="body2" paragraph>
                            3. Or click "Generate Live Plots" to use PocketVNA hardware
                        </Typography>
                        <Typography variant="body2" paragraph>
                            4. Select different plot types to view various S-parameter analyses
                        </Typography>
                        <Typography variant="body2">
                            5. Download the data for further analysis
                        </Typography>
                    </CardContent>
                </StyledCard>
            )}
        </div>
    );
};

export default BatteryScopeC;